import React, { useState, useEffect } from 'react';

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  content: { background: 'white', borderRadius: '16px', width: '100%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' },
  closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#94a3b8', cursor: 'pointer', padding: '4px', lineHeight: 1 },
  body: { flex: 1, overflowY: 'auto', padding: '20px' },
  subtitle: { margin: '0 0 20px', fontSize: '14px', color: '#64748b' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#334155' },
  input: { width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' },
  inputAutre: { width: '100%', padding: '10px 12px', border: '1px solid #93c5fd', borderRadius: '8px', fontSize: '14px', background: '#eff6ff', boxSizing: 'border-box', marginTop: '6px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' },
  footer: { display: 'flex', gap: '12px', padding: '16px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' },
  btnSecondary: { flex: 1, padding: '12px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', background: 'white', border: '1px solid #d1d5db', color: '#374151' },
  btnPrimary: { flex: 1, padding: '12px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none', color: 'white' }
};

const NATURE_OPTIONS = [
  'Disjoncteur Général de Branchement',
  'Réseau de distribution public Basse Tension',
  'Réseau interne du client',
  'Groupe électrogène'
];
const ORIGINE_OPTIONS = [
  "Distributeur public d'énergie ENEDIS",
  'Tableau Général Basse Tension (TGBT)',
  'Tableau divisionnaire',
  'Coffret de chantier',
  'Armoire électrique principale'
];
const TYPE_COMPTAGE_OPTIONS = [
  'Comptage à Puissance Limitée',
  'Puissance Limitée (ex Tarif bleu)',
  'Puissance Surveillée (ex Tarif Jaune)'
];
const PUISSANCE_OPTIONS = ['12', '18', '24', '30', '36', '54', '75', '100'];
const COURANT_CC_OPTIONS = ['3', '5', '6', '9', '10', '12', '15', '18', '20', '25'];

const EMPTY_DATA = {
  nature_designation: '',
  nature_designation_autre: '',
  origine: '',
  origine_autre: '',
  localisation: '',
  type_comptage: '',
  type_comptage_autre: '',
  tension: '400',
  puissance: '',
  puissance_autre: '',
  intensite: '',
  schema_liaison_terre: 'TNC',
  courant_court_circuit: '',
  courant_court_circuit_autre: ''
};

// Si une donnée pré-existante contient une valeur libre (hors liste),
// on la replie sur "Autre" + champ _autre pour l'édition.
const buildFromInitial = (initial) => {
  if (!initial) return { ...EMPTY_DATA };
  const merged = { ...EMPTY_DATA, ...initial };
  const presets = {
    nature_designation: NATURE_OPTIONS,
    origine: ORIGINE_OPTIONS,
    type_comptage: TYPE_COMPTAGE_OPTIONS,
    puissance: PUISSANCE_OPTIONS,
    courant_court_circuit: COURANT_CC_OPTIONS
  };
  Object.entries(presets).forEach(([field, options]) => {
    const value = merged[field];
    if (value && !options.includes(value)) {
      merged[`${field}_autre`] = value;
      merged[field] = 'Autre';
    }
  });
  return merged;
};

function mergePresetIntoFormState(preset) {
  if (!preset || typeof preset !== 'object') return { ...EMPTY_DATA };
  const { type_source: _ts, ...rest } = preset;
  return { ...EMPTY_DATA, ...rest };
}

function SourceBTModalMobile({ isOpen, onClose, onSave, initialData, initialPreset = null }) {
  const [data, setData] = useState(() => buildFromInitial(initialData));

  useEffect(() => {
    if (!isOpen) return;
    if (initialPreset) {
      setData(buildFromInitial(mergePresetIntoFormState(initialPreset)));
    } else {
      setData(buildFromInitial(initialData));
    }
  }, [isOpen, initialPreset, initialData]);

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const isValid = !!data.nature_designation
    && !!data.origine
    && !!data.localisation
    && !!data.puissance
    && (data.nature_designation !== 'Autre' || !!data.nature_designation_autre)
    && (data.origine !== 'Autre' || !!data.origine_autre)
    && (data.puissance !== 'Autre' || !!data.puissance_autre);

  const handleSave = () => {
    if (!isValid) return;

    const finalNatureDesignation = data.nature_designation === 'Autre' ? data.nature_designation_autre : data.nature_designation;

    // Détermine automatiquement type_source (public/prive) selon la nature
    let typeSource = 'public';
    const lower = (finalNatureDesignation || '').toLowerCase();
    if (lower.includes('réseau de distribution public')) {
      typeSource = 'public';
    } else if (lower.includes('réseau interne')) {
      typeSource = 'prive';
    }

    const finalData = {
      ...data,
      nature_designation: finalNatureDesignation,
      type_source: typeSource,
      origine: data.origine === 'Autre' ? data.origine_autre : data.origine,
      type_comptage: data.type_comptage === 'Autre' ? data.type_comptage_autre : data.type_comptage,
      puissance: data.puissance === 'Autre' ? data.puissance_autre : data.puissance,
      courant_court_circuit: data.courant_court_circuit === 'Autre' ? data.courant_court_circuit_autre : data.courant_court_circuit
    };

    onSave(finalData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.content} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={{margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b'}}>
            {initialPreset ? 'Source BT — modèle DBR standard' : 'Ajouter une source Basse Tension'}
          </h2>
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={modalStyles.body}>
          <p style={modalStyles.subtitle}>
            {initialPreset
              ? 'Vérifiez ou modifiez les valeurs préremplies, puis enregistrez pour passer au disjoncteur de branchement.'
              : "Renseignez les informations de la source d'énergie."}
          </p>

          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Nature et désignation *</label>
            <select value={data.nature_designation} onChange={(e) => handleChange('nature_designation', e.target.value)} style={modalStyles.input}>
              <option value="">Sélectionner...</option>
              {NATURE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              <option value="Autre">Autre (saisie libre)</option>
            </select>
            {data.nature_designation === 'Autre' && (
              <input type="text" value={data.nature_designation_autre} onChange={(e) => handleChange('nature_designation_autre', e.target.value)} style={modalStyles.inputAutre} placeholder="Précisez la nature..." />
            )}
          </div>

          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Origine de l'installation vérifiée *</label>
            <select value={data.origine} onChange={(e) => handleChange('origine', e.target.value)} style={modalStyles.input}>
              <option value="">Sélectionner...</option>
              {ORIGINE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              <option value="Autre">Autre (saisie libre)</option>
            </select>
            {data.origine === 'Autre' && (
              <input type="text" value={data.origine_autre} onChange={(e) => handleChange('origine_autre', e.target.value)} style={modalStyles.inputAutre} placeholder="Précisez l'origine..." />
            )}
          </div>

          <div style={modalStyles.formRow}>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Localisation *</label>
              <input type="text" value={data.localisation} onChange={(e) => handleChange('localisation', e.target.value)} style={modalStyles.input} placeholder="Ex: Placard technique" />
            </div>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Type de comptage</label>
              <select value={data.type_comptage} onChange={(e) => handleChange('type_comptage', e.target.value)} style={modalStyles.input}>
                <option value="">Sélectionner...</option>
                {TYPE_COMPTAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                <option value="Autre">Autre (saisie libre)</option>
              </select>
              {data.type_comptage === 'Autre' && (
                <input type="text" value={data.type_comptage_autre} onChange={(e) => handleChange('type_comptage_autre', e.target.value)} style={modalStyles.inputAutre} placeholder="Précisez le type de comptage..." />
              )}
            </div>
          </div>

          <div style={modalStyles.formRow}>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Tension nominale (V) *</label>
              <select value={data.tension} onChange={(e) => handleChange('tension', e.target.value)} style={modalStyles.input}>
                <option value="230">230 V (monophasé)</option>
                <option value="400">400 V (triphasé)</option>
              </select>
            </div>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Puissance souscrite (kVA) *</label>
              <select value={data.puissance} onChange={(e) => handleChange('puissance', e.target.value)} style={modalStyles.input}>
                <option value="">Sélectionner...</option>
                {PUISSANCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} kVA</option>)}
                <option value="Autre">Autre (saisie libre)</option>
              </select>
              {data.puissance === 'Autre' && (
                <input type="number" value={data.puissance_autre} onChange={(e) => handleChange('puissance_autre', e.target.value)} style={modalStyles.inputAutre} placeholder="Précisez la puissance..." />
              )}
            </div>
          </div>

          <div style={modalStyles.formRow}>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Intensité de réglage disjoncteur (A)</label>
              <input type="number" value={data.intensite} onChange={(e) => handleChange('intensite', e.target.value)} style={modalStyles.input} placeholder="Ex: 63" />
            </div>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Schéma de liaison à la terre *</label>
              <select value={data.schema_liaison_terre} onChange={(e) => handleChange('schema_liaison_terre', e.target.value)} style={modalStyles.input}>
                <option value="TT">TT</option>
                <option value="TN-C">TN-C</option>
                <option value="TN-S">TN-S</option>
                <option value="IT">IT</option>
                <option value="TNC">TNC</option>
              </select>
            </div>
          </div>

          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Courant de court circuit triphasé (kA)</label>
            <select value={data.courant_court_circuit} onChange={(e) => handleChange('courant_court_circuit', e.target.value)} style={modalStyles.input}>
              <option value="">Sélectionner...</option>
              {COURANT_CC_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} kA</option>)}
              <option value="Autre">Autre (saisie libre)</option>
            </select>
            {data.courant_court_circuit === 'Autre' && (
              <input type="number" value={data.courant_court_circuit_autre} onChange={(e) => handleChange('courant_court_circuit_autre', e.target.value)} style={modalStyles.inputAutre} placeholder="Précisez la valeur en kA..." />
            )}
          </div>
        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.btnSecondary} onClick={onClose}>Annuler</button>
          <button style={{...modalStyles.btnPrimary, opacity: isValid ? 1 : 0.5}} onClick={handleSave} disabled={!isValid}>Enregistrer et configurer le disjoncteur</button>
        </div>
      </div>
    </div>
  );
}

export default SourceBTModalMobile;
